import React from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import Spacings from '~/modules/ui-kit/spacings';
import formMessages from '~/components/form-messages';
import LanguageName from '~/components/LanguageName';
import LanguageAutocompleteField from '~/components/LanguageAutocompleteField';
import { useAppForm } from '~/components/Form';
import messages from './messages';

const schema = (intl: IntlShape) =>
  z
    .object({
      name: z
        .string({ error: intl.formatMessage(formMessages.required) })
        .min(1, intl.formatMessage(formMessages.required)),
      language: z
        .string({ error: intl.formatMessage(formMessages.required) })
        .min(1, intl.formatMessage(formMessages.required)),
      circuitOverseerName: z.string(),
      circuitOverseerEmail: z.string(),
    })
    .superRefine((data, ctx) => {
      const name = data.circuitOverseerName?.trim() ?? '';
      const email = data.circuitOverseerEmail?.trim() ?? '';
      const hasName = name.length > 0;
      const hasEmail = email.length > 0;

      if (hasName && !hasEmail) {
        ctx.addIssue({
          code: 'custom',
          path: ['circuitOverseerEmail'],
          message: intl.formatMessage(formMessages.required),
        });
        return;
      }

      if (hasEmail && !hasName) {
        ctx.addIssue({
          code: 'custom',
          path: ['circuitOverseerName'],
          message: intl.formatMessage(formMessages.required),
        });
        return;
      }

      if (hasName && name.length < 3) {
        ctx.addIssue({
          code: 'custom',
          path: ['circuitOverseerName'],
          message: intl.formatMessage(formMessages.minLength),
        });
      }

      if (!z.email().safeParse(email).success && email.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['circuitOverseerEmail'],
          message: intl.formatMessage(formMessages.invalidEmailAddress),
        });
      }
    });

type FormValues = {
  name: string;
  language: string;
  circuitOverseerName?: string;
  circuitOverseerEmail?: string;
};

type CircuitFormProps = {
  onSubmit: (values: FormValues) => void;
  initialValues?: {
    name?: string;
    language?: string;
    circuitOverseerName?: string;
    circuitOverseerEmail?: string;
  };
  children?: React.ReactNode;
};

export const CircuitForm = (props: CircuitFormProps) => {
  const intl = useIntl();
  const form = useAppForm({
    defaultValues: {
      name: '',
      language: '',
      circuitOverseerName: '',
      circuitOverseerEmail: '',
      ...props.initialValues,
    },
    validators: {
      onChange: schema(intl),
    },
    onSubmit({ value }) {
      props.onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Inset>
        <Spacings.Stack scale="l">
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.name} />}
              />
            )}
          </form.AppField>
          {props.initialValues && props.initialValues.language ? (
            <ListItem style={{ paddingLeft: 0, paddingRight: 0 }}>
              <ListItemText
                primary={
                  <LanguageName languageCode={props.initialValues.language} />
                }
                secondary={<FormattedMessage {...messages.language} />}
              />
            </ListItem>
          ) : (
            <form.Field name="language">
              {(field) => (
                <LanguageAutocompleteField
                  input={{
                    value: field.state.value,
                    onChange: (languageKey) => {
                      field.handleChange(languageKey || '');
                    },
                  }}
                  label={<FormattedMessage {...messages.language} />}
                />
              )}
            </form.Field>
          )}
          <form.AppField name="circuitOverseerName">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.circuitOverseerName} />}
              />
            )}
          </form.AppField>
          <form.AppField name="circuitOverseerEmail">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.circuitOverseerEmail} />}
              />
            )}
          </form.AppField>
          {props.children}
        </Spacings.Stack>
      </Spacings.Inset>
      {/* Hidden submit button to allow form submission via Enter key */}
      <input type="submit" hidden />
    </form>
  );
};

export default CircuitForm;
